import React, { useState, useEffect } from 'react';
import { ChatConfig, ModelConfig } from '../types';
import './ConfigPanel.css';

interface ConfigPanelProps {
  config: ChatConfig;
  onConfigChange: (config: ChatConfig) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  config,
  onConfigChange,
  isOpen,
  onToggle
}) => {
  const [localConfig, setLocalConfig] = useState<ChatConfig>(config);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [customModelName, setCustomModelName] = useState('');

  // 当配置改变时更新本地状态
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleSave = () => {
    // 配置已经实时应用，这里只需要关闭面板
    onToggle();
  };

  const handleCancel = () => {
    setLocalConfig(config);
    setErrorMessage('');
    onToggle();
  };

  const updateConfig = (updates: Partial<ChatConfig>) => {
    const updatedConfig = { ...localConfig, ...updates };
    setLocalConfig(updatedConfig);
    // 立即应用配置变化
    onConfigChange(updatedConfig);
    setErrorMessage('');
  };

  const addModel = (model: ModelConfig) => {
    if (!localConfig.models || !Array.isArray(localConfig.models)) {
      updateConfig({
        models: [model]
      });
      return;
    }
    
    if (localConfig.models.some(m => m.value === model.value)) {
      setErrorMessage('该模型已存在');
      return;
    }

    updateConfig({
      models: [...localConfig.models, model]
    });
  };

  const removeModel = (modelValue: string) => {
    if (!localConfig.models || !Array.isArray(localConfig.models)) {
      return;
    }
    
    const updatedModels = localConfig.models.filter(m => m.value !== modelValue);
    let newCurrentModel = localConfig.currentModel;
    
    // 如果删除的是当前模型，切换到第一个可用模型
    if (modelValue === localConfig.currentModel && updatedModels.length > 0) {
      newCurrentModel = updatedModels[0].value;
    }

    updateConfig({
      models: updatedModels,
      currentModel: newCurrentModel
    });
  };

  const handleAddCustomModel = () => {
    if (!customModelName.trim()) {
      setErrorMessage('模型名称不能为空');
      return;
    }

    if (localConfig.models && Array.isArray(localConfig.models)) {
      if (localConfig.models.some(m => m.value === customModelName.trim())) {
        setErrorMessage('该模型已存在');
        return;
      }
    }

    addModel({
      name: customModelName.trim(),
      value: customModelName.trim(),
      displayName: customModelName.trim()
    });
    setCustomModelName('');
    setErrorMessage('');
  };

  const quickModels: ModelConfig[] = [
    { name: 'GPT-3.5 Turbo', value: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo' },
    { name: 'GPT-4', value: 'gpt-4', displayName: 'GPT-4' },
    { name: 'GPT-4 Turbo', value: 'gpt-4-turbo', displayName: 'GPT-4 Turbo' },
    { name: 'Claude-3 Sonnet', value: 'claude-3-sonnet', displayName: 'Claude-3 Sonnet' },
    { name: 'Claude-3 Opus', value: 'claude-3-opus', displayName: 'Claude-3 Opus' }
  ];

  return (
    <div className={`config-panel ${isOpen ? 'open' : ''}`}>
      <div className="config-header">
        <h3>配置设置</h3>
        <button className="close-btn" onClick={onToggle}>
          ✕
        </button>
      </div>
      
      <div className="config-content">
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
            <button className="error-close" onClick={() => setErrorMessage('')}>
              ✕
            </button>
          </div>
        )}

        <div className="config-section">
          <h4>API 配置</h4>
          <div className="form-group">
            <label>API URL:</label>
            <input
              type="url"
              value={localConfig.baseUrl}
              onChange={(e) => updateConfig({ baseUrl: e.target.value })}
              placeholder="https://api.openai.com/v1/chat/completions"
            />
          </div>
          <div className="form-group">
            <label>API Key:</label>
            <input
              type="password"
              value={localConfig.apiKey}
              onChange={(e) => updateConfig({ apiKey: e.target.value })}
              placeholder="输入你的API密钥"
            />
          </div>
        </div>

        <div className="config-section">
          <h4>模型管理 ({localConfig.models?.length || 0})</h4>
          <div className="model-list">
            {localConfig.models && Array.isArray(localConfig.models) && localConfig.models.map(model => (
              <div key={model.value} className="model-item">
                <span className="model-name">{model.displayName}</span>
                <span className="model-value">({model.value})</span>
                {model.value === localConfig.currentModel && (
                  <span className="current-model-badge">当前</span>
                )}
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => removeModel(model.value)}
                >
                  删除
                </button>
              </div>
            ))}
            {(!localConfig.models || localConfig.models.length === 0) && (
              <p className="no-models">暂无配置的模型</p>
            )}
          </div>
        </div>

        <div className="config-section">
          <h4>添加模型</h4>
          <div className="quick-models">
            <span className="quick-models-label">快速添加:</span>
            <div className="quick-model-buttons">
              {quickModels.map((model) => (
                <button
                  key={model.value}
                  type="button"
                  className={`quick-model-btn ${
                    localConfig.models && localConfig.models.some(m => m.value === model.value) ? 'added' : ''
                  }`}
                  onClick={() => addModel(model)}
                  disabled={localConfig.models && localConfig.models.some(m => m.value === model.value)}
                >
                  {model.displayName}
                </button>
              ))}
            </div>
          </div>

          <div className="custom-model">
            <h5>自定义模型</h5>
            <div className="form-group" style={{ display: 'flex', gap: 8 }}>
              <label style={{ flexShrink: 0 }}>模型名称:</label>
              <input
                type="text"
                placeholder="输入模型名称，如: gpt-3.5-turbo"
                value={customModelName}
                onChange={e => setCustomModelName(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    handleAddCustomModel();
                  }
                }}
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-primary btn-sm"
                type="button"
                style={{ marginLeft: 4 }}
                onClick={handleAddCustomModel}
                disabled={!customModelName.trim()}
              >
                添加
              </button>
            </div>
          </div>
        </div>

        <div className="config-section">
          <h4>当前选择的模型</h4>
          <select
            value={localConfig.currentModel}
            onChange={(e) => updateConfig({ currentModel: e.target.value })}
            disabled={!localConfig.models || localConfig.models.length === 0}
          >
            {!localConfig.models || localConfig.models.length === 0 ? (
              <option value="">请先添加模型</option>
            ) : (
              localConfig.models.map(model => (
                <option key={model.value} value={model.value}>
                  {model.displayName}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="config-actions">
          <button className="btn btn-primary" onClick={handleSave}>
            保存配置
          </button>
          <button className="btn btn-secondary" onClick={handleCancel}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigPanel; 